import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { MODULE_ACCESS } from "../utils/adminRoles";

const AdminAuthContext = createContext(null);

const resolveAdminRole = (profile) => {
  if (!profile) return null;
  if (profile.adminEnabled === false) return null;
  return profile.adminRole || null;
};

const normalizeModuleAccess = (overrides) => {
  const next = {};
  Object.keys(MODULE_ACCESS).forEach((roleKey) => {
    const baseList = Array.isArray(MODULE_ACCESS[roleKey])
      ? MODULE_ACCESS[roleKey]
      : [];
    const overrideList = Array.isArray(overrides?.[roleKey])
      ? overrides[roleKey]
      : baseList;
    const normalizedList =
      roleKey === "developer" ? [...baseList, ...overrideList] : overrideList;
    next[roleKey] = Array.from(new Set(normalizedList));
  });
  return next;
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [moduleAccess, setModuleAccess] = useState(MODULE_ACCESS);
  const [moduleAccessLoading, setModuleAccessLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async (authUser) => {
    if (!authUser) {
      setProfile(null);
      setRole(null);
      return;
    }

    try {
      setError(null);
      const userRef = doc(db, "users", authUser.uid);
      const userSnap = await getDoc(userRef);
      const data = userSnap.exists() ? userSnap.data() : null;
      setProfile(data);
      setRole(resolveAdminRole(data));
    } catch (err) {
      console.error("Failed to load admin profile", err);
      setError(err);
      setProfile(null);
      setRole(null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      setUser(authUser || null);
      setLoading(true);
      await loadProfile(authUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [loadProfile]);

  const signIn = useCallback(async (email, password) => {
    setError(null);
    return signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signOutAdmin = useCallback(async () => {
    setError(null);
    return signOut(auth);
  }, []);

  const loadModuleAccess = useCallback(async () => {
    setModuleAccessLoading(true);
    try {
      const settingsRef = doc(db, "adminSettings", "moduleAccess");
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        const data = settingsSnap.data() || {};
        const roles = data.roles || data.moduleAccess || null;
        setModuleAccess(normalizeModuleAccess(roles));
      } else {
        setModuleAccess(MODULE_ACCESS);
      }
    } catch (err) {
      console.error("Failed to load module access", err);
      setModuleAccess(MODULE_ACCESS);
    } finally {
      setModuleAccessLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModuleAccess();
  }, [loadModuleAccess]);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    await loadProfile(user);
    setLoading(false);
  }, [loadProfile, user]);

  const refreshModuleAccess = useCallback(async () => {
    await loadModuleAccess();
  }, [loadModuleAccess]);

  const value = useMemo(
    () => ({
      user,
      profile,
      role,
      moduleAccess,
      moduleAccessLoading,
      loading,
      error,
      signIn,
      signOut: signOutAdmin,
      refreshProfile,
      refreshModuleAccess,
    }),
    [
      user,
      profile,
      role,
      moduleAccess,
      moduleAccessLoading,
      loading,
      error,
      signIn,
      signOutAdmin,
      refreshProfile,
      refreshModuleAccess,
    ]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return ctx;
};
